/**
 * 管理模式 API
 * 对应 foundation-service (7092)
 */

import { unifiedApi } from '@/utils/unifiedApi';
import handleApiResponse from '@/utils/apiResponseHandler';

// ==================== 类型定义 ====================

/** 管理模式状态 */
export interface AdminModeStatus {
  is_in_admin_mode: boolean;
  session_id?: number;
  entered_at?: string;
  last_activity_at?: string;
  timeout_minutes: number;
  remaining_seconds?: number;
  has_permission: boolean;
}

/** 进入管理模式响应 */
export interface EnterAdminModeResponse {
  success: boolean;
  session_id: number;
  entered_at: string;
  timeout_minutes: number;
  message?: string;
}

/** 退出管理模式响应 */
export interface ExitAdminModeResponse {
  success: boolean;
  message?: string;
}

/** 心跳响应 */
export interface HeartbeatResponse {
  success: boolean;
  remaining_seconds: number;
}

/** 租户配置 */
export interface TenantSettings {
  admin_mode: AdminModeSettings;
  security: SecuritySettings;
  theme: ThemeSettings;
  branding: BrandingSettings;
}

/** 管理模式配置 */
export interface AdminModeSettings {
  enabled: boolean;
  timeout_minutes: number;
}

/** 安全配置 */
export interface SecuritySettings {
  password_min_length: number;
  password_require_special: boolean;
  login_fail_lock_count: number;
  login_fail_lock_minutes: number;
}

/** 主题配置 */
export interface ThemeSettings {
  primary_color: string;
  admin_primary_color: string;
}

/** 品牌配置 */
export interface BrandingSettings {
  system_name: string;
  logo_url: string;
  favicon_url: string;
  copyright: string;
}

// ==================== API 函数 ====================

/** 获取管理模式状态 */
export const getAdminModeStatus = async () => {
  return handleApiResponse(
    unifiedApi.service('adminMode').get<{ data: AdminModeStatus }>('/admin-mode/status')
  );
};

/** 进入管理模式 */
export const enterAdminMode = async () => {
  return handleApiResponse(
    unifiedApi.service('adminMode').post<{ data: EnterAdminModeResponse }>('/admin-mode/enter', {})
  );
};

/** 退出管理模式 */
export const exitAdminMode = async (reason: string = 'manual') => {
  return handleApiResponse(
    unifiedApi.service('adminMode').post<{ data: ExitAdminModeResponse }>('/admin-mode/exit', { reason })
  );
};

/** 心跳保活 */
export const heartbeat = async () => {
  return handleApiResponse(
    unifiedApi.service('adminMode').post<{ data: HeartbeatResponse }>('/admin-mode/heartbeat', {})
  );
};

/** 获取租户配置 */
export const getTenantSettings = async () => {
  return handleApiResponse(
    unifiedApi.service('tenantSettings').get<{ data: TenantSettings }>('/tenant/settings')
  );
};

/** 更新租户配置 */
export const updateTenantSettings = async (settings: Partial<TenantSettings>) => {
  return handleApiResponse(
    unifiedApi.service('tenantSettings').put<{ data: { success: boolean; message: string; settings: TenantSettings } }>('/tenant/settings', settings)
  );
};

/** 获取管理模式配置 */
export const getAdminModeSettings = async () => {
  return handleApiResponse(
    unifiedApi.service('tenantSettings').get<{ data: AdminModeSettings }>('/tenant/settings/admin-mode')
  );
};

/** 更新管理模式配置 */
export const updateAdminModeSettings = async (settings: Partial<AdminModeSettings>) => {
  return handleApiResponse(
    unifiedApi.service('tenantSettings').put<{ data: { success: boolean; message: string; settings: TenantSettings } }>('/tenant/settings/admin-mode', settings)
  );
};

// 默认导出
const adminModeApi = {
  getStatus: getAdminModeStatus,
  enter: enterAdminMode,
  exit: exitAdminMode,
  heartbeat,
  getTenantSettings,
  updateTenantSettings,
  getAdminModeSettings,
  updateAdminModeSettings,
};

export default adminModeApi;
